/**
 * Bidi isolation for server-composed text (M5.1). A time range, a date, an email address, a
 * timezone or a code inside Arabic text is wrapped in U+2066 (LEFT-TO-RIGHT ISOLATE) …
 * U+2069 (POP DIRECTIONAL ISOLATE), so «14:00–15:00» never renders as 15:00–14:00 and an
 * address never splits around the @. The web wraps the same fragments in <bdi dir="ltr">.
 */

export const LRI = '⁦';
export const PDI = '⁩';

/** Fragments that must keep their left-to-right order inside any paragraph. */
export const LTR_FRAGMENT_RE = new RegExp(
  [
    // email addresses
    String.raw`[\w.+-]+@[\w-]+(?:\.[\w-]+)+`,
    // IANA timezones
    String.raw`(?:Asia|Europe|America|Africa|Australia|Pacific|Atlantic|Indian|Etc)\/[A-Za-z_]+(?:\/[A-Za-z_]+)?`,
    // time ranges and single times with an optional zone in brackets: 14:00–15:00, 9:30-10:00, 14:00
    String.raw`\d{1,2}:\d{2}(?:\s*[–-]\s*\d{1,2}:\d{2})?(?:\s*\([A-Za-z/_]+\))?`,
    // ISO dates and dates with a Latin month: 2026-09-29, 29 Sep 2026, Sep 29
    String.raw`\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})?)?`,
    String.raw`\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\.?(?:\s+\d{4})?`,
    // codes: six or more upper-case letters and digits with at least one digit
    String.raw`(?<![\p{L}\p{N}])(?=[A-Z0-9]*\d)[A-Z0-9]{6,}(?![\p{L}\p{N}])`,
    // URLs
    String.raw`https?:\/\/[^\s)»"']+`,
  ].join('|'),
  'gu',
);

/** Wrap one fragment. */
export function isolate(fragment: string): string {
  return `${LRI}${fragment}${PDI}`;
}

/** Wrap every LTR fragment of the text; already-isolated fragments are left alone. */
export function isolateSpans(text: string): string {
  if (!text) return text;
  return text.replace(LTR_FRAGMENT_RE, (m, offset: number, whole: string) => {
    const before = whole[offset - 1];
    const after = whole[offset + m.length];
    if (before === LRI || after === PDI) return m;
    return isolate(m);
  });
}

/** The text without isolate marks (for matching, keys and tests). */
export function stripIsolates(text: string): string {
  return text.replace(/[⁦-⁩]/gu, '');
}

export interface BidiPart {
  text: string;
  /** True for a fragment that must render left to right. */
  ltr: boolean;
}

/** Split text into plain parts and LTR fragments, for a component that wraps the latter. */
export function splitIsolates(text: string): BidiPart[] {
  const plain = stripIsolates(text);
  const parts: BidiPart[] = [];
  let last = 0;
  for (const m of plain.matchAll(LTR_FRAGMENT_RE)) {
    const at = m.index ?? 0;
    if (at > last) parts.push({ text: plain.slice(last, at), ltr: false });
    parts.push({ text: m[0], ltr: true });
    last = at + m[0].length;
  }
  if (last < plain.length) parts.push({ text: plain.slice(last), ltr: false });
  return parts;
}
